import React from 'react';
import PropTypes from 'prop-types';
import 'react-dates/initialize';

import isMobile from '../util/mobile.js';
import { calendarPhrases } from '../locale/phrases.js';
import { adjustedCalendarWidth } from './calendarWidthHelper.js';
import { disableBodyScroll, enableBodyScroll } from '../util/bodyScroll.js';

import asyncImport from '../async/AsyncImport.jsx';
import CalendarTopBar from './CalendarTopBar.jsx';
import SingleDatePickerPlaceholder from './SingleDatePickerPlaceholder.jsx';

import './reactDates.css';

const AsyncSingleDatePicker = asyncImport(() => import('react-dates/lib/components/SingleDatePicker.js'));

export default class SingleDateCalendar extends React.Component {
    constructor(props) {
        super(props);

        this.onDateChange = this.onDateChange.bind(this);
        this.onFocusChange = this.onFocusChange.bind(this);
        this.clearCalendar = this.clearCalendar.bind(this);
        this.closeCalendar = this.closeCalendar.bind(this);
        this.transitionOverlay = this.transitionOverlay.bind(this);

        this.mobile = isMobile();
        this.state = {
            focused: typeof window !== 'undefined' && window.location.search === `?calendar=${this.props.id}` ? true : null,
        };
    }

    componentDidMount() {
        if (typeof this.props.onRef !== 'undefined') {
            this.props.onRef(this);
        }
        if (this.mobile && this.state.focused === true) {
            disableBodyScroll();
        } else {
            enableBodyScroll();
        }
        this.mobile = isMobile();
        window.addEventListener('popstate', this.transitionOverlay);
    }

    componentDidUpdate() {
        if (this.props.id === 'pickupDate' && document.getElementsByClassName('DayPicker_transitionContainer').length > 0) {
            const scrollPos = document.getElementsByClassName('DayPicker_transitionContainer')[0].scrollTop;
            window.sessionStorage.setItem('scrollPos', scrollPos);
        }

        if (this.props.id === 'returnDate' && document.getElementsByClassName('DayPicker_transitionContainer').length > 0) {
            document.getElementsByClassName('DayPicker_transitionContainer')[0].scrollTop = window.sessionStorage.getItem('scrollPos');
        }
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.transitionOverlay);
    }

    transitionOverlay() {
        if (this.mobile) {
            if (window.history.state == null) {
                // This means it's page load
                return;
            }
            this.setState({ focused: window.location.search === `?calendar=${this.props.id}` });
        }
    }

    onDateChange(date) {
        if (date) {
            this.props.onDateChange(date);
        }
    }

    onFocusChange({ focused }) {
        if (focused) {
            this.props.onFocusChange({ calendarId: this.props.id });
        }
        this.setState({ focused });
        if (this.mobile) {
            if (focused === true) {
                disableBodyScroll();
                if (this.props.pushUrlContext) {
                    this.props.pushUrlContext(`calendar=${this.props.id}`);
                }
            } else {
                enableBodyScroll();
                if (this.props.popUrlContext) {
                    this.props.popUrlContext();
                }
            }
        }
    }

    clearCalendar() {
        this.setState({ focused: true });
        this.props.onDateReset(null);
    }

    closeCalendar() {
        this.setState({ focused: false });
        if (this.mobile) {
            window.sessionStorage.setItem('scrollPos', 0);
            if (this.props.popUrlContext) {
                this.props.popUrlContext();
            }
        }
    }

    renderCalendarInfo() {
        return (
            this.mobile && (
                <CalendarTopBar closeCalendar={this.closeCalendar} clearCalendar={this.clearCalendar} enableBodyScroll={enableBodyScroll} />
            )
        );
    }

    render() {
        return (
            <AsyncSingleDatePicker
                id={this.props.id}
                placeholder={this.props.placeholder}
                date={this.props.value}
                disabled={this.props.disabled}
                focused={this.state.focused && this.props.showCalendar}
                onDateChange={this.onDateChange}
                onFocusChange={this.onFocusChange}
                isDayHighlighted={this.props.isDayHighlighted}
                initialVisibleMonth={this.props.initialVisibleMonth}
                withFullScreenPortal={this.mobile}
                orientation={this.mobile ? 'verticalScrollable' : 'horizontal'}
                numberOfMonths={this.mobile ? 6 : 2}
                anchorDirection={this.props.anchorDirection}
                keepOpenOnDateSelect={this.mobile}
                daySize={this.mobile ? adjustedCalendarWidth() : 39}
                calendarInfoPosition={this.mobile ? 'top' : null}
                renderCalendarInfo={this.renderCalendarInfo.bind(this)}
                customCloseIcon={<div />}
                readOnly={this.mobile}
                phrases={calendarPhrases()}
                displayFormat={() => 'DD.MM.YYYY'}>
                <SingleDatePickerPlaceholder
                    id={this.props.id}
                    placeholder={this.props.placeholder}
                    value={this.props.value && this.props.value.format('DD.MM.YYYY')}
                />
            </AsyncSingleDatePicker>
        );
    }
}

SingleDateCalendar.propTypes = {
    id: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    onFocusChange: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    onDateReset: PropTypes.func.isRequired,
    value: PropTypes.object, // moment instance
    isDayHighlighted: PropTypes.func,
    initialVisibleMonth: PropTypes.func,
    disabled: PropTypes.bool,
    onRef: PropTypes.func,
    anchorDirection: PropTypes.string,
    showCalendar: PropTypes.bool,
    pushUrlContext: PropTypes.func,
    popUrlContext: PropTypes.func,
};

SingleDateCalendar.defaultProps = {
    placeholder: '',
    disabled: false,
    anchorDirection: 'left',
};
